import * as React from 'react'
import { OfferedServiceBase } from '../../../../services/json-schemas/base/offered_service'
import { ServiceBase } from '../../../../services/json-schemas/base/service'
import { offeredServiceIsCalculatorEntryOnlyForFirstEvent } from '../../../../services/models/offered-services.service'
import { CastingStore, StoreProps } from '../../../../services/stores/casting.store'
import { I18nService } from '../../../../services/utils/i18n.service'
import { Select } from '../../../shared/select.component'
import StepperInputComponent from '../../../shared/stepper-input.component'
import { Input } from '../casting-styled.component'
import CastingDurationCalculatorFeedbackComponent from './casting-duration-calculator-feedback.component'

interface State {
  metierKey: string
  schema: ServiceBase['metier'][string]
  paramMetier: OfferedServiceBase['param_metier'][string]
}

const i18nScoped = new I18nService(require('./casting-duration-calculator-legacy.component.yml'))

export default class CastingDurationCalculatorLegacyComponent extends React.Component<StoreProps, State> {
  constructor (props: StoreProps) {
    super(props)
    const { data: { offeredService } } = this.props
    const metierKey = offeredService.param_metier ? Object.keys(offeredService.param_metier)[0] : null
    const schema = metierKey ? offeredService.service.metier[metierKey] : null
    const paramMetier = metierKey ? offeredService.param_metier[metierKey] : null
    this.state = { metierKey, schema, paramMetier }
  }

  componentDidMount () {
    const { props: { data: { variableMetier, calculatorResult }, store }, state: { metierKey, paramMetier, schema } } = this

    if (!variableMetier || !variableMetier[metierKey]) {
      // loads the initial state of the calculator, by hydrating the fields with default params
      const newVariableMetier: CastingStore['data']['variableMetier'] = { [metierKey]: { fields: {} } }

      for (const key in schema.fields.schema.properties) {
        if (!this.isInputEnabled(key)) continue
        const value = schema.fields.schema.properties[key]
        const hasDuration = parseFloat(paramMetier.params[`${value.title}_duration`] as string) > 0
        if (hasDuration || value.default) newVariableMetier[metierKey].fields[value.title] = parseFloat(value.default)
      }

      this.setVariableMetier(newVariableMetier)
    } else if (variableMetier && !calculatorResult) {
      // this case occurs when editing an existing casting that uses the calculator
      store.set({
        variableMetier,
        calculatorResult: this.computeResult(variableMetier, false),
        calculatorResultFirstEvent: this.computeResult(variableMetier, true)
      }, { runCallbacks: false, runResets: false })
    }
  }

  private updateState (value: any, name: string) {
    const { state: { metierKey }, props: { data: { variableMetier } } } = this

    if (value !== variableMetier[metierKey].fields[name]) {
      const newVariableMetier = { ...variableMetier }
      newVariableMetier[metierKey] = { ...newVariableMetier[metierKey] }
      newVariableMetier[metierKey].fields = { ...newVariableMetier[metierKey].fields, [name]: value }
      this.setVariableMetier(newVariableMetier)
    }
  }

  private setVariableMetier (variableMetier: StoreProps['data']['variableMetier']) {
    const store = this.props.store
    store.set({ variableMetier, calculatorResult: this.computeResult(variableMetier, false), calculatorResultFirstEvent: this.computeResult(variableMetier, true) })
    store.initSlotWithDuration(store.getMinDuration())
  }

  /** Compute the number of hours based on inputs */
  private computeResult (variableMetier = this.props.data.variableMetier, computeForFirstSlot: boolean) {
    const { props: { store, data: { offeredService: { duration_min, duration_step } } }, state: { metierKey, paramMetier } } = this
    let sumDuration = 0

    for (const key in variableMetier[metierKey].fields) {
      if (!this.isInputEnabled(key)) continue
      const durationInHours = parseFloat(paramMetier.params[`${key}_duration`] as string)
      const durationStep = parseInt(paramMetier.params[`${key}_duration_step`] as string, 10) || 1
      const isDurationValid = typeof durationInHours === 'number' && durationInHours > 0
      if (isDurationValid) sumDuration += (+variableMetier[metierKey].fields[key] * durationInHours / durationStep) * 3600
    }

    // coef is applyed after all other durations are computed
    for (const key in variableMetier[metierKey].fields) {
      if (!this.isInputEnabled(key)) continue
      if (!computeForFirstSlot && offeredServiceIsCalculatorEntryOnlyForFirstEvent(key)) continue
      const coef = parseFloat(paramMetier.params[`${key}_coef`] as string)
      const isCoefValid = variableMetier[metierKey].fields[key] === true && typeof coef === 'number' && coef > 0
      if (isCoefValid) sumDuration += sumDuration * coef
    }

    sumDuration = Math.min(Math.max(sumDuration, duration_min), store.getMaxDuration())
    sumDuration = Math.ceil(sumDuration / duration_step) * duration_step
    return sumDuration
  }

  private formatInputValue (name: string, unit: string, val: number) {
    if (unit) return i18nScoped.t(`inputs.${name}_input`, { count: val, unit: i18nScoped.t(`units.${unit}`) })
    return i18nScoped.t(`inputs.${name}_input`, { count: val })
  }

  private isInputEnabled (key: string) {
    const { schema, paramMetier } = this.state

    switch (schema.fields.schema.properties[key].type) {
      case 'number': return +paramMetier.params[`${key}_duration`] > 0
      case 'string': return +paramMetier.params[key] === 1
      case 'checkbox': return ((paramMetier.params[`${key}_coef`] && +paramMetier.params[`${key}_coef`] > 0) || (paramMetier.params[`${key}_label`] && +paramMetier.params[`${key}_label`] > 0))
      case 'selectmultiple': return paramMetier.params[key] && paramMetier.params[key].length > 0
      case 'coef': return false // TODO: check if still in use
      case 'select': return false // TODO: check if still in use
    }
  }

  private renderInputs () {
    const { props: { data: { variableMetier } }, state: { schema, paramMetier, metierKey } } = this

    return schema.fields.schema.required.filter(key => this.isInputEnabled(key)).map(key => {
      const value = schema.fields.schema.properties[key]
      switch (value.type) {
        case 'number':
          return <div key={ `wrp_${value.title}` }>
            <div>{ i18nScoped.t(`inputs.${value.title}_input_description`, {}, '') }</div>
            <StepperInputComponent
              name={ value.title }
              value={ variableMetier[metierKey].fields[value.title] as number }
              onChange={ val => this.updateState(val, value.title) }
              key={ value.title }
              min={ 0 }
              max={ 10000 }
              step={ parseInt(paramMetier.params[`${key}_duration_step`] as string, 10) || 1 }
              formatter={ val => this.formatInputValue(key, paramMetier.params[`${key}_duration_unit`] as string, val) }
              style={{ marginBottom: '0.4em' }} />
          </div>
        case 'string':
          return <Input
            type="text"
            key={ value.title }
            value={ variableMetier[metierKey].fields[value.title] as string ?? '' }
            placeholder={ i18nScoped.t(`inputs.${value.title}_input.placeholder`) }
            onChange={ val => this.updateState(val.target.value, value.title) } />
        case 'checkbox':
          return <div className="inputCheckbox" key={ value.title }>
            <input id={ `${value.title}_id` } type="checkbox" checked={ !!variableMetier[metierKey].fields[value.title] } onChange={ event => this.updateState(event.target.checked, value.title) } />
            <label htmlFor={ `${value.title}_id` }>{ i18nScoped.t(`inputs.${value.title}_input.label`) }</label>
          </div>
        case 'selectmultiple':
          return <div className="u-mbs" key={ value.title }>
            <Select
              value={ (variableMetier[metierKey].fields[value.title] || []).map((value: any) => ({ value })) }
              options={ (paramMetier.params[key] as string[]).map((value: any) => ({ value })) }
              placeholder={ i18nScoped.t(`inputs.${value.title}_input.placeholder`) }
              getOptionLabel={ val => i18nScoped.t(`inputs.ground_type_input.${val.value}`) }
              onChange={ choices => this.updateState(choices.map(_ => _.value), value.title) }
              isMulti />
          </div>
        case 'coef':
        case 'select':
          return null // TODO: check if those cases should render an input in the calculator
      }
    })
  }

  render () {
    const { state: { metierKey, paramMetier }, props: { data: { variableMetier } } } = this
    if (!metierKey || !paramMetier || !variableMetier || !variableMetier[metierKey]) return null

    return <div id="js-test-duration-calculator">
      { this.renderInputs() }
      
      <CastingDurationCalculatorFeedbackComponent
        { ...this.props }
        resultForAllSlots={ this.computeResult(variableMetier, false) } />
    </div>
  }
}
